import {
  isDateObject,
  formattedDurationInHours,
  normalizeSingleDayStartEndDates,
  castToDateWithoutSeconds,
  castToBoolean,
} from '../../../../../lib/util';

class PrimaryClaimEditorController {
  constructor(
    $q,
    $scope,
    $translate,
    LanguageService,
    DateTimeService,
    LogService
  ) {
    "ngInject";
    this.$q              = $q;
    this.$scope          = $scope;
    this.$translate      = $translate;
    this.LanguageService = LanguageService;
    this.DateTimeService = DateTimeService;
    this.LogService      = LogService;

    /**
     * Date Pickers
     */
    this._datePickersOpened = {};
    this._datePickersOptions = {};

    let datePickerNames = ['claim_date'];
    let defaultDatePickerOptions = {
      minDate: null,
      maxDate:null
    };
    for (let datePickerName of datePickerNames) {
      this._datePickersOpened[datePickerName] = false;
      this._datePickersOptions[datePickerName] = {...defaultDatePickerOptions};
    }

    this.timeLimitException = false;
    this.timeLimitExceptionAcknowledged = false;

    this._errors = [];

    this._translatedMessages = {
      'MESSAGE_PLEASE_REDUCE_CLAIM_END_TIME': '',
      'BREAK_ACKNOWLEDGEMENT': '',
      'MEAL_BREAK_ACKNOWLEDGEMENT': '',
      'REST_BREAK_ACKNOWLEDGEMENT': '',
    };

    this.$scope.$on('LanguageServiceChangedLanguage', () => this._updateTranslations());
  }

  _updateTranslations() {
    const promises = Object.keys(this._translatedMessages).map(key => {
      return this.$translate(key)
        .then(translated => {
          this.LogService.debug(`Translated ${key} to ${translated}`);
          this._translatedMessages[key] = translated;
          return [key, translated];
        });
    });
    return this.$q.all(promises);
  }

  translateMessage(key) {
    if (!this._translatedMessages[key]) {
      this._translatedMessages[key] = key;
      this._updateTranslations();
    }
    return this._translatedMessages[key];
  }

  get errors() {
    return this._errors;
  }

  _pushError(err) {
    this._errors.push(err);
  }

  _clearErrors() {
    this._errors = [];
  }

  set position(position) {
    this._position = position;
    // TODO: Don't update position here, move to model
    if (position) {
      if (!position.claim_date) {
        position.claim_date = position.start_date;
      }
      if (!position.actual_start) {
        position.actual_start = position.start_time;
      }
      if (!position.actual_end) {
        position.actual_end = position.end_time;
      }
    }

    // Separate out showMealBreak for compliance vs non-compliance.
    if (position.compliance_enabled) {
      // Compliance
      this.showMealBreak = position && position.meal_break_eligible && position.meal_break_acknowledgement !== null
      if (this.showMealBreak && position.meal_break_acknowledgement.hasOwnProperty('id')) {
        this.showMealBreak = (parseInt(position.meal_break_acknowledgement.id) === 3)
      }
    }
    else {
      // Non-Compliance
      this.showMealBreak = position && !!(position.meal_break_start || position.meal_break_end)
    }

    // showRestBreak is only valid for Compliance.
    if (position.compliance_enabled && position.rest_break_eligible) {
      this.showRestBreak = position
        && position.rest_break_eligible
        && (position.rest_break_expected_qty > 0)
        && (position.rest_break_acknowledgement !== null)
      if (this.showRestBreak && position.rest_break_acknowledgement.hasOwnProperty('id')) {
        this.showRestBreak = (parseInt(position.rest_break_acknowledgement.id) === 3)
      }
    }
    else {
      this.showRestBreak = false
    }
  }

  get position() {
      /**
       * Repopulate the shift segment object if an id is set when coming back from the server.
       * This is really only needed so the form repopulates correctly.
       */
      if (this._position && this._position.shift_segments_id && !this._position.shiftSegment)
      {
          if (this.staffUser && this.staffUser.shift_segments)
          {
             let segment = this.staffUser.shift_segments.filter(segment => parseInt(segment.id) == parseInt(this._position.shift_segments_id));
             this._position.shiftSegment = segment.pop();
          }
      }

    return this._position;
  }

  get checkins() {
    if (!this._position || !this._position.checkin || this._position.checkin.length === 0) {
      return {
        first: false,
        last: false,
        duration: false
      };
    }
    const first = this._position.checkin[0];
    const last = this._position.checkin[this._position.checkin.length - 1];

    // Bail early if not possible to have checkin/out combination
    if (last.type === 'in') {
      return {
        first: false,
        last: false,
        duration: false,
      };
    }

    const durationMs = new Date(last.timestamp) - new Date(first.timestamp);

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    let formattedDuration;
    if (hours === 0) {
      formattedDuration = `${minutes} min${minutes !== 1 ? 's' : ''}`;
    } else if (minutes === 0) {
      formattedDuration = `${hours} hr${hours !== 1 ? 's' : ''}`;
    } else {
      formattedDuration = `${hours} hr${hours !== 1 ? 's' : ''} ${minutes} min${minutes !== 1 ? 's' : ''}`;
    }

    return {
      first: this.DateTimeService.parseMomentWithTimezone(first.timestamp).format('h:mm A'),
      last: this.DateTimeService.parseMomentWithTimezone(last.timestamp).format('h:mm A'),
      duration: formattedDuration
    };
  }

  get canApplyCheckinTimes() {
    return this.staffUser.use_apply_checkins_button
  }

  applyCheckinTimesToClaim() {

    if (!this._position || !this._position.checkin || this._position.checkin.length === 0) {
      return void 0
    }
    const first = this._position.checkin[0];
    const last = this._position.checkin[this._position.checkin.length - 1];

    const firstTimestamp = new Date(first.timestamp);
    const lastTimestamp = new Date(last.timestamp);

    this._position.actual_start = firstTimestamp;
    this._position.actual_end = lastTimestamp;

    this.positionActualStartTimeShiftedForLocalTimezone()
    this.positionActualEndTimeShiftedForLocalTimezone()
  }

  get positionStartTimeInEventTimezone() {
    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    const positionStartTime = this.position && this.position.start_time;
    if (!eventTimezone || !positionStartTime) {
      return positionStartTime;
    }
    const moment = this.DateTimeService.parseMomentWithTimezone(positionStartTime, eventTimezone);
    return moment;
  }

  get positionEndTimeInEventTimezone() {
    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    const positionEndTime = this.position && this.position.end_time;
    if (!eventTimezone || !positionEndTime) {
      return positionEndTime;
    }
    const moment = this.DateTimeService.parseMomentWithTimezone(positionEndTime, eventTimezone);
    return moment;
  }

  set positionClaimDate(date) {
    if (!this.position) {
      return null;
    }
    this.position.claim_date = date;
  }

  get positionClaimDate() {
    if (!this.position) {
      return null;
    }
    return this.position.claim_date;
  }

  get positionActualStartTimeShiftedForLocalTimezone() {
    const positionActualStartTime = this.position && this.position.actual_start;
    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    if (!positionActualStartTime || !eventTimezone) {
      return positionActualStartTime;
    }
    const shifted = this.DateTimeService.shiftDateObjectFromLocal(positionActualStartTime, eventTimezone);

    if (this._lastPositionActualStartTimeShiftedForLocalTimezone
      && this._lastPositionActualStartTimeShiftedForLocalTimezone.getTime() === shifted.getTime()
    ) {
      return this._lastPositionActualStartTimeShiftedForLocalTimezone;
    }

    this._lastPositionActualStartTimeShiftedForLocalTimezone = shifted;
    return shifted;
  }

  set positionActualStartTimeShiftedForLocalTimezone(date) {
    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    if (!date) {
      this.position.actual_start = date;
      return;
    }

    date = castToDateWithoutSeconds(date, eventTimezone);
    this.position.actual_start = this.DateTimeService.unshiftDateObjectFromLocal(date, eventTimezone);
  }

  get positionActualEndTimeShiftedForLocalTimezone() {
    const positionActualEndTime = this.position && this.position.actual_end;
    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    if (!positionActualEndTime || !eventTimezone) {
      return positionActualEndTime;
    }
    const shifted = this.DateTimeService.shiftDateObjectFromLocal(positionActualEndTime, eventTimezone);

    if (this._lastPositionActualEndTimeShiftedForLocalTimezone
      && this._lastPositionActualEndTimeShiftedForLocalTimezone.getTime() === shifted.getTime()
    ) {
      return this._lastPositionActualEndTimeShiftedForLocalTimezone;
    }

    this._lastPositionActualEndTimeShiftedForLocalTimezone = shifted;
    return shifted;
  }

  set positionActualEndTimeShiftedForLocalTimezone(date) {
    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    if (!date) {
      this.position.actual_end = date;
      return;
    }

    date = castToDateWithoutSeconds(date, eventTimezone);
    this.position.actual_end = this.DateTimeService.unshiftDateObjectFromLocal(date, eventTimezone);
  }

  get scheduledDuration() {
    if (this.position && isDateObject(this.position.end_time) && isDateObject(this.position.start_time)) {
      return formattedDurationInHours(this.position.start_time, this.position.end_time);
    } else {
      return '';
    }
  }

  set positionMealBreakStartShiftedForLocalTimezone(time) {
    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    if (!time) {
      this.position.meal_break_start = time;
      return;
    }

    time = castToDateWithoutSeconds(time, eventTimezone);
    const unshiftedTime = this.DateTimeService.unshiftDateObjectFromLocal(time, eventTimezone);
    this.position.meal_break_start = unshiftedTime;
    this._normalizeMealBreakStartEndDates();
  }

  get positionMealBreakStartShiftedForLocalTimezone() {
    const positionMealBreakStart = this.position && this.position.meal_break_start;
    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    if (!positionMealBreakStart || !eventTimezone) {
      return positionMealBreakStart;
    }
    const shifted = this.DateTimeService.shiftDateObjectFromLocal(positionMealBreakStart, eventTimezone);
    if (this._lastPositionMealBreakStartShiftedForLocalTimezone
      && this._lastPositionMealBreakStartShiftedForLocalTimezone.getTime() === shifted.getTime()
    ) {
      return this._lastPositionMealBreakStartShiftedForLocalTimezone;
    }

    this._lastPositionMealBreakStartShiftedForLocalTimezone = shifted;
    return shifted;
  }

  set positionMealBreakEndShiftedForLocalTimezone(time) {
    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    if (!time) {
      this.position.meal_break_end = time;
      return;
    }

    time = castToDateWithoutSeconds(time, eventTimezone);
    const unshiftedTime = this.DateTimeService.unshiftDateObjectFromLocal(time, eventTimezone);
    this.position.meal_break_end = unshiftedTime;
    this._normalizeMealBreakStartEndDates();
  }

  get positionMealBreakEndShiftedForLocalTimezone() {
    const positionMealBreakEnd = this.position && this.position.meal_break_end;
    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    if (!positionMealBreakEnd || !eventTimezone) {
      return positionMealBreakEnd;
    }
    const shifted = this.DateTimeService.shiftDateObjectFromLocal(positionMealBreakEnd, eventTimezone);

    if (this._lastPositionMealBreakEndShiftedForLocalTimezone
      && this._lastPositionMealBreakEndShiftedForLocalTimezone.getTime() === shifted.getTime()
    ) {
      return this._lastPositionMealBreakEndShiftedForLocalTimezone;
    }

    this._lastPositionMealBreakEndShiftedForLocalTimezone = shifted;
    return shifted;
  }

  /**
   * Resets meal start and end dates to match the position start date
   */
  _normalizeMealBreakStartEndDates() {
    if (!this.position || !this.position.meal_break_start || !this.position.meal_break_end) {
      return;
    }

    const actualStart = this.position.actual_start;
    let oldStart = this.position.meal_break_start;
    let oldEnd = this.position.meal_break_end;

    if (!actualStart || !oldStart || !oldEnd) {
      return;
    }

    // Ensure date part of oldStart matches actual start date for position
    oldStart.setDate(actualStart.getDate());
    oldStart.setMonth(actualStart.getMonth());
    oldStart.setFullYear(actualStart.getFullYear());

    let [newStart, newEnd] = normalizeSingleDayStartEndDates(oldStart, oldEnd);
    if (newStart.getTime() !== oldStart.getTime()) {
      this.position.meal_break_start = newStart;
    }
    if (newEnd.getTime() !== oldEnd.getTime()) {
      this.position.meal_break_end = newEnd;
    }
  }

  /**
   * Format rest break dates and times to the appropriate values.
   */
  _normalizeRestBreakStartEndDates(index) {
    if (!this.position || !this.position.rest_break_start[index] || !this.position.rest_break_end[index]) {
      return
    }

    const actualStart = this.position.actual_start
    if (!actualStart) {
      return
    }

    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    const actualDate = actualStart.getDate()
    const actualMonth = actualStart.getMonth()
    const actualYear = actualStart.getFullYear()

    // Both start and end share keys so data matches up. Use the eventTimezone, since this is
    // what is used to convert it before saving to the database.
    const oldStart = this.position.rest_break_start[index]
    const oldEnd = this.position.rest_break_end[index]
    if (!oldStart || !oldEnd) {
      return
    }

    // Ensure date parts matches actual start date for position.
    oldStart.setDate(actualDate)
    oldStart.setMonth(actualMonth)
    oldStart.setFullYear(actualYear)

    let [newStart, newEnd] = normalizeSingleDayStartEndDates(oldStart, oldEnd)

    if (newStart.getTime() !== oldStart.getTime()) {
      this.position.rest_break_start[index] = newStart
    }
    if (newEnd.getTime() !== oldEnd.getTime()) {
      this.position.rest_break_end[index] = newEnd
    }
  }

  positionDurationForFieldPrefix(prefix) {
    if (this.position) {
      let start = this.position[`${prefix}_start`];
      let end = this.position[`${prefix}_end`];
      [start, end] = normalizeSingleDayStartEndDates(start, end);
      return formattedDurationInHours(start, end);
    } else {
      return '';
    }
  }
  positionDurationForRestBreak(index) {
    if (this.position) {
      this._normalizeRestBreakStartEndDates(index)

      let start = this.position.rest_break_start[index];
      let end = this.position.rest_break_end[index];
      [start, end] = normalizeSingleDayStartEndDates(start, end);
      return formattedDurationInHours(start, end);
    } else {
      return '';
    }
  }

  set staffUser(staffUser) {
    this._staffUser = staffUser;
  }

  set canEdit(canEdit) {
    this._canEdit = canEdit;
  }

  get canEdit() {
    return this._canEdit;
  }

  get staffUser() {
    return this._staffUser;
  }

  get showShiftSegment()
  {
    let show = false;
    if (this.event)
    {
      if (this.event.opt_out_travel_standard == 'f' || this.event.opt_out_travel_tiered=='f')
      {
        show = true;
      }
    }
    return show;
  }

    get showRouteSegment()
    {
        let show = false;

        if (this.position && this.position.shiftSegment && this.primaryClaim && this.primaryClaim.type && this.primaryClaim.type.collect_route_segments=='t')
        {
          show = true;
        }

        if (!show)
        {
            if (this.primaryClaim && this.primaryClaim.routeSegment)
            {
                this.primaryClaim.routeSegment = null;
                this.primaryClaim.route_segments_id = null;
            }
        }

        return show;
    }

  updateShiftSegmentId()
  {
    if (this.position && this.position.shiftSegment)
    {
      this.position.shift_segments_id = this.position.shiftSegment.id;
    }

    if (this.primaryClaim && this.primaryClaim.routeSegment)
    {
        this.primaryClaim.routeSegment = null;
        this.primaryClaim.route_segments_id = null;
    }

    this.$scope.$emit('primaryShiftSegmentUpdated');
  }

  updateRouteSegmentId()
  {
    if (this.primaryClaim && this.primaryClaim.routeSegment)
    {
      this.primaryClaim.route_segments_id = this.primaryClaim.routeSegment.id;
    }
  }



  getShiftSegments()
  {
      let segments = this.getAllShiftSegments();

      segments = this.filterShiftSegmentsUsedOnDate(segments);

      segments = this.addCurrentlySelectedShiftSegment(segments);

      return segments;
  }

  addCurrentlySelectedShiftSegment(segments)
  {
      if (this.position && this.position.shiftSegment && this.position.shiftSegment.id)
      {
          if (!isNaN(parseInt(this.position.shiftSegment.id)))
          {
              let exists = null;
              for(let i=0;i<segments.length;i++)
              {
                  if (parseInt(segments[i].id) === parseInt(this.position.shiftSegment.id))
                  {
                      exists = segments[i];
                  }
              }

              if (!exists)
              {
                  segments.push(this.position.shiftSegment);
              }

          }
      }

      return segments;
  }

  getAllShiftSegments()
  {
      let segments = [];

      if (this._staffUser && this._staffUser.shift_segments)
      {
          segments = this._staffUser.shift_segments;
      }

      return segments;
  }

  filterShiftSegmentsUsedOnDate(segments)
  {
      let shift_segment_ids_for_date = [];
      let primary_claim_date = (this.position) ? this.position.claim_date : null;
      let primary_position_id = (this.position) ? this.position.id : null;

      if (primary_claim_date)
      {
          if (this.staffUser && this.staffUser._shiftSegmentsByDate)
          {
              shift_segment_ids_for_date = this.staffUser.getShiftSegmentsUsedByDate(primary_claim_date, primary_position_id);

              let pushed4 = false;

              if (shift_segment_ids_for_date.indexOf(1) !== -1)
              {
                  pushed4 = true;
                  shift_segment_ids_for_date.push(4);
              }

              if (shift_segment_ids_for_date.indexOf(3) !== -1 )
              {
                  pushed4 = true;
                  shift_segment_ids_for_date.push(4,shift_segment_ids_for_date.indexOf(3));
              }

              if (shift_segment_ids_for_date.indexOf(4) !== -1 && !pushed4)
              {
                  shift_segment_ids_for_date.push(1);
                  shift_segment_ids_for_date.push(3);
              }

              if (shift_segment_ids_for_date.length)
              {
                  segments = segments.filter(segment => shift_segment_ids_for_date.indexOf(parseInt(segment.id)) === -1);
              }
          }
      }

      return segments;
  }



    getRouteSegments()
    {
        let segments = [];

        if (this._staffUser && this._staffUser.route_segments)
        {
            segments = this._staffUser.route_segments;
        }

        segments = segments.filter(
            segment =>
            segment.definition == 'ROUTE_SEGMENT_TIME_BETWEEN' ||
            segment.definition == 'ROUTE_SEGMENT_TIME_HOME_TO_FIRST' ||
            segment.definition == 'ROUTE_SEGMENT_TIME_LAST_TO_HOME'
        );

        if (this.position && this.position.shiftSegment)
        {

            switch(this.position.shiftSegment.definition)
            {

                case 'SHIFT_SEGMENT_FIRST':
                    segments = segments.filter(
                        segment =>
                        segment.definition == 'ROUTE_SEGMENT_DISTANCE_HOME_TO_FIRST'  ||
                        segment.definition == 'ROUTE_SEGMENT_TIME_HOME_TO_FIRST'

                    );
                    break;

                case 'SHIFT_SEGMENT_NOT_FIRST_OR_LAST':
                    segments = segments.filter(
                        segment =>
                        segment.definition == 'ROUTE_SEGMENT_DISTANCE_BETWEEN'  ||
                        segment.definition == 'ROUTE_SEGMENT_TIME_BETWEEN'
                    );
                    break;

                case 'SHIFT_SEGMENT_LAST':
                    segments = segments.filter(
                        segment =>
                        segment.definition == 'ROUTE_SEGMENT_DISTANCE_BETWEEN'  ||
                        segment.definition == 'ROUTE_SEGMENT_TIME_BETWEEN' ||
                        segment.definition == 'ROUTE_SEGMENT_DISTANCE_LAST_TO_HOME'  ||
                        segment.definition == 'ROUTE_SEGMENT_TIME_LAST_TO_HOME'
                    );
                    break;

                case 'SHIFT_SEGMENT_ONLY':
                    segments = segments.filter(
                        segment =>
                        segment.definition == 'ROUTE_SEGMENT_DISTANCE_LAST_TO_HOME'  ||
                        segment.definition == 'ROUTE_SEGMENT_TIME_LAST_TO_HOME' ||
                        segment.definition == 'ROUTE_SEGMENT_DISTANCE_HOME_TO_FIRST'  ||
                        segment.definition == 'ROUTE_SEGMENT_TIME_HOME_TO_FIRST'
                    );
                    break;
            }
        }


        let used_segment_ids = this.event.used_segment_ids;

        let route_segments_ids_for_date = [];

        /*
        We're commenting this out for now because at this moment,
        we don't want to check other shifts on the same date
        to filter down the available route segments

        if (this.staffUser && this.staffUser._routeSegmentsByDate)
        {
            if (this.position.claim_date)
            {
                route_segments_ids_for_date = this.staffUser.getRouteSegmentsUsedByDate(this.position.claim_date);

                if (route_segments_ids_for_date.length)
                {
                    segments = segments.filter(segment => route_segments_ids_for_date.indexOf(parseInt(segment.id)) === -1);
                }
            }
        }

        */

        segments = segments.filter(segment => used_segment_ids.indexOf(parseInt(segment.id)) === -1);

        if (this.primaryClaim && this.primaryClaim.route_segments_id)
        {

            let selected_segment = this.staffUser.route_segments.filter(segment => parseInt(segment.id) == parseInt(this.primaryClaim.route_segments_id));
            let the_selected_segment = selected_segment.pop();
            if (!isNaN(parseInt(the_selected_segment.id)) && segments.indexOf(the_selected_segment) === -1)
            {
                segments.push(the_selected_segment);
            }
        }

        return segments;
    }

  set primaryClaim(claim) {
    this._primaryClaim = claim;
    this.updateIsException();
  }

  get primaryClaim() {

      if (this._primaryClaim && this._primaryClaim.route_segments_id && !this._primaryClaim.routeSegment)
      {
          if (this.staffUser && this.staffUser.route_segments)
          {
              let segment = this.staffUser.route_segments.filter(segment => parseInt(segment.id) == parseInt(this._primaryClaim.route_segments_id));
              this._primaryClaim.routeSegment = segment.pop();
          }
      }

      this._primaryClaim.inExceptionState = this.isExceptionState();

    return this._primaryClaim;
  }


  isExceptionState(){

      if (this.timeLimitException && !this.timeLimitExceptionAcknowledged)
      {
          return true;
      }

      return false;
  }

  updateIsException() {
      // Limit in admin is in minutes but calculated duration here is in hours
      const limit = this.getExceptionByName('time_based_units_limit');
      const minutes = parseFloat(this.claimDurationInHours * 60);
      this.timeLimitException = !!(limit && parseFloat(limit.value) > 0 && parseFloat(limit.value) < (minutes) && !this.timeLimitExceptionAcknowledged);
  }

  get claimDurationInHours() {
    return this.positionDurationForFieldPrefix('actual');
  }

  getExceptionByName(name) {
      let except = null;
      if (this.staffUser && this.staffUser.payroll) {
        except = this.staffUser.payroll.find(el => el.name === name);
      }
      return except;
  }

  get primaryClaimTypes() {
    let result = this.event && this.event.primaryClaimTypes || [];

    console.log('--pre filtered claim types', result);
    // Filter out any claim types that are not allowed for this position (based upon position_access).
    if (this.position.position_id) {
      result = result.filter(claimType => !!claimType.position_access.find(accessId => parseInt(accessId) === parseInt(this.position.position_id)));
    }

    // Check if any of the claim types in result have a property named "use_for_forecasting" and if it is set to true.
    // A claim type exists with a true value for this property, then filter the result to only include claim types with "use_for_forecasting" set to true.
    const hasForecastingClaimType = result.some(claimType => claimType.hasOwnProperty('use_for_forecasting') && claimType.use_for_forecasting === true);
    if (hasForecastingClaimType) {
      result = result.filter(claimType => claimType.hasOwnProperty('use_for_forecasting') && castToBoolean(claimType.use_for_forecasting) === true);
    }

    /**
     * Filter out claim types that are already used by any secondary claims
     */
    if (this.event.used_claim_type_ids) {
      result = result.filter(claimType =>
        (claimType.category && claimType.category.constant === 'CLAIM_CATEGORY_AMOUNT') ||
        this.event.used_claim_type_ids.indexOf(parseInt(claimType.id)) === -1 ||
        (this.primaryClaim.type && parseInt(claimType.id) === parseInt(this.primaryClaim.type.id)) ||
        (castToBoolean(claimType.collect_route_segments) && (!castToBoolean(this.event.opt_out_travel_standard) || !castToBoolean(this.event.opt_out_travel_tiered)))
      );
    }

    /**
     * Filter out auto created claim types UNLESS the primary claim is already using it
     */
    result = result.filter(claimType => (!castToBoolean(claimType.auto_create)) || (this.primaryClaim.type && parseInt(this.primaryClaim.type.id) === parseInt(claimType.id)));
    return result;
  }

  get primaryClaimMessage() {
    return this.translateMessage('MESSAGE_PLEASE_REDUCE_CLAIM_END_TIME');
  }

  get meal_break_threshold_hours() {
    return this.position.meal_break_threshold_hours
  }

  get mealBreakMessage() {
    if (this.showMealBreakAcknowledgement) {
      if (this.position.meal_break_eligible) {
        return `This shift is eligible for a meal break of ${this.position.meal_break_duration_mins} minutes`
      }
      return `This shift is not eligible for a paid meal break because it is less than ${this.position.meal_break_threshold_hours} hour(s).`
    }
    else {
      return false
    }
  }
  get restBreakMessage() {
    if (this.showRestBreakAcknowledgement) {
      if (this.position.rest_break_eligible) {
        return `This shift is eligible for ${this.position.rest_break_expected_qty} rest break(s) of ${this.position.rest_break_duration_mins} minutes each`
      }
      return `This shift is not eligible for paid rest break(s) because it is less than ${this.position.rest_break_threshold_hours} hour(s).`
    }
    else return false
  }

  /**
   * Used specifically for Compliance acknowledgements. If Compliance is disabled,
   * this should always return false (so that the non-Compliance fields will display).
   *
   * @returns {boolean}
   */
  get showMealBreakAcknowledgement() {
    if (this.position.compliance_enabled
      && this.position.meal_break_eligible
      && this.position.meal_break_threshold_hours !== null
      && this.position.meal_break_duration_mins !== null
    ) {
      return true
    }
    return false
  }

  /**
   * Only relevant for Compliance, since rest breaks only exist when Compliance is enabled.
   *
   * @returns {boolean}
   */
  get showRestBreakAcknowledgement() {
    if (this.position.compliance_enabled
      && this.position.rest_break_eligible
      && this.position.rest_break_threshold_hours !== null
      && this.position.rest_break_duration_mins !== null
    ) {
      return true
    }
    return false
  }

  get breakAcknowledgement() {
    return this.translateMessage('BREAK_ACKNOWLEDGEMENT');
  }
  get mealBreakAcknowledgement() {
    return this.translateMessage('MEAL_BREAK_ACKNOWLEDGEMENT');
  }
  get restBreakAcknowledgement() {
    return this.translateMessage('REST_BREAK_ACKNOWLEDGEMENT');
  }

  get mealBreakOptions() {
    return this.position.meal_break_ack_options || {}
  }
  get restBreakOptions() {
    return this.position.rest_break_ack_options || {}
  }

  mealBreakChanged() {
    if (parseInt(this.position.meal_break_acknowledgement.id) === 3) {
      this._showMealBreak = true
      this.showMealBreakChanged()
    }
    else {
      this._showMealBreak = false
      this.showMealBreakChanged()
    }
  }
  restBreakChanged() {
    if (parseInt(this.position.rest_break_acknowledgement.id) === 3) {
      this._showRestBreak = true
      this.showRestBreakChanged()
    }
    else {
      this._showRestBreak = false
      this.showRestBreakChanged()
    }
  }

  set formName(name) {
    this._formName = name;
  }

  get formName() {
    return this._formName;
  }

  /****************************
   * Validation
   ****************************/

  validate() {
    let form = this.$scope.primaryClaimForm;
    if (form.$valid) {
      this._clearErrors();
      return true;
    } else {
      this.LogService.warn("form not valid! $error:", form.$error);
      this._clearErrors();
      for (let key in form.$error) {
        for (let idx in form.$error[key]) {
          let errorInfo = form.$error[key][idx];
          let name = errorInfo.$name.substring(0,1).toUpperCase() + errorInfo.$name.substring(1);
          let error;
          switch (key) {
            case 'required':
              error = name + ' is required';
              break;
            default:
              error = 'Error with ' + name;
              break;
          }
          this._pushError(error);
        }
      }
      this.LogService.warn("validate() errors:", this.errors);
      return false;
    }
  }


  /****************************
   * Meal break collapsers
   ****************************/

  set showMealBreak(show) {
    this._showMealBreak = show;
  }

  get showMealBreak() {
    return this._showMealBreak;
  }

  showMealBreakChanged() {
    if (this.showMealBreak) {
      // Set default value to position start/end
      this.position.meal_break_start = this.position.actual_start;
      this.position.meal_break_end = this.position.actual_end;
    } else {
      this.position.meal_break_start = null;
      this.position.meal_break_end = null;
    }
  }

  set showRestBreak(show) {
    this._showRestBreak = show;
  }
  get showRestBreak() {
    return this._showRestBreak;
  }

  showRestBreakChanged() {
    if (this.showRestBreak) {
      // No default value for rest break start/end times
    } else {
      for (let i = 1; i < this.position.rest_break_expected_qty; i++) {
        this._position.rest_break_start[i] = null
        this._position.rest_break_end[i] = null
      }
    }
  }

  set breakAcknowledged(val) {
    this._breakAcknowledged = val;
  }

  get breakAcknowledged() {
    return this._breakAcknowledged;
  }

  /****************************
   * Date picker modals
   ****************************/

  get datePickersOpened() {
    return this._datePickersOpened;
  }

  get datePickersOptions() {
    return this._datePickersOptions;
  }

  openDatePicker(datePickerName) {
    if (this.event) {
      if (this.event.recap_available_events_start_time && this.event.recap_available_events_end_time){
        this._datePickersOptions = {
          minDate: Math.max.apply(null, [new Date(this.event.programs_start_date), new Date(this.event.recap_available_events_start_time)]),
          maxDate: Math.min.apply(null, [new Date(this.event.programs_end_date), new Date(this.event.recap_available_events_end_time)]),
          showWeeks: false,
        };
      } else {
        if (this.event.programs_start_date && this.event.programs_end_date) {
          this._datePickersOptions = {
            minDate: Math.max.apply(null, [new Date(this.event.programs_start_date)]),
            maxDate: Math.min.apply(null, [new Date(this.event.programs_end_date)]),
            showWeeks: false,
          };
        } else {
          this._datePickersOptions = {
            showWeeks: false,
          };
        }
      }
    } else {
      return; // No program or event type chosen, no date can be chosen
    }

    this._datePickersOpened[datePickerName] = true;
  }
}

export default PrimaryClaimEditorController;
